import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Types
export interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: 'admin' | 'vendor' | 'customer' | null;
  vendor_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface VendorProfile {
  id: string;
  company_name: string;
  description: string | null;
  full_name: string | null;
  mobile_number: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  gst_number: string | null;
  website_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  logo_url: string | null;
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  onboarding_completed: boolean | null;
  show_gst_on_invoice: boolean | null;
  invoice_terms_and_conditions: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserWithProfile {
  id: string;
  email?: string;
  profile: Profile | null;
  vendor: VendorProfile | null;
}

export interface AuthSignInData {
  email: string;
  password: string;
  captchaToken?: string;
}

export interface AuthSignInResponse extends UserWithProfile {
  requiresVerification?: boolean;
  email?: string;
}

export interface AuthSignUpData {
  email: string;
  password: string;
  captchaToken?: string;
}

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface UpdateVendorProfileData {
  company_name?: string;
  description?: string;
  full_name?: string;
  mobile_number?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  gst_number?: string;
  website_url?: string;
  instagram_url?: string;
  facebook_url?: string;
  logo_url?: string;
  invoice_terms_and_conditions?: string;
}



export interface FileUploadData {
  file: File;
  bucket?: string;
  folder?: string;
}

export interface DeleteFileData {
  file_path: string;
  bucket?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  captchaToken?: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  password: string;
  confirmPassword: string;
}



// API Functions
export const authApi = {
  // Get current user profile
  getProfile: async (): Promise<{ profile: Profile; vendor: VendorProfile | null }> => {
    const response = await fetch('/api/auth/profile');
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch profile');
    }
    const data = await response.json();
    return data.data;
  },

  // Update user profile
  updateProfile: async (data: UpdateProfileData): Promise<Profile> => {
    const response = await fetch('/api/auth/profile', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update profile');
    }
    const result = await response.json();
    return result.data;
  },

  // Get vendor profile
  getVendorProfile: async (): Promise<VendorProfile> => {
    const response = await fetch('/api/auth/vendor');
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch vendor profile');
    }
    const data = await response.json();
    return data.data;
  },

  // Update vendor profile
  updateVendorProfile: async (data: UpdateVendorProfileData): Promise<VendorProfile> => {
    const response = await fetch('/api/auth/vendor', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update vendor profile');
    }
    const result = await response.json();
    return result.data;
  },



  // Sign in
  signIn: async (data: AuthSignInData): Promise<AuthSignInResponse> => {
    const response = await fetch('/api/auth/signin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sign in');
    }
    const result = await response.json();
    return result.data;
  },

  // Sign up
  signUp: async (data: AuthSignUpData): Promise<UserWithProfile> => {
    const response = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sign up');
    }
    const result = await response.json();
    return result.data;
  },

  // Sign out
  signOut: async (): Promise<void> => {
    const response = await fetch('/api/auth/signout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to sign out');
    }
  },

  // Upload file
  uploadFile: async (data: FileUploadData): Promise<{ path: string; public_url: string; file_name: string }> => {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.bucket) formData.append('bucket', data.bucket);
    if (data.folder) formData.append('folder', data.folder);

    const response = await fetch('/api/storage/upload', {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload file');
    }
    const result = await response.json();
    return result.data;
  },

  // Delete file
  deleteFile: async (data: DeleteFileData): Promise<void> => {
    const response = await fetch('/api/storage/delete', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete file');
    }
  },

  // Change password
  changePassword: async (data: ChangePasswordData): Promise<{ message: string }> => {
    const response = await fetch('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to change password');
    }
    const result = await response.json();
    return result.data;
  },

  // Forgot password
  forgotPassword: async (data: ForgotPasswordData): Promise<{ message: string }> => {
    const response = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send reset email');
    }
    const result = await response.json();
    return result.data;
  },

  // Reset password
  resetPassword: async (data: ResetPasswordData): Promise<{ message: string }> => {
    const response = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to reset password');
    }
    const result = await response.json();
    return result.data;
  },


};

// React Query Hooks
export function useProfile(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['profile'],
    queryFn: authApi.getProfile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Simple retry count instead of complex logic
    enabled: options?.enabled !== false
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Profile updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useVendorProfile() {
  return useQuery({
    queryKey: ['vendorProfile'],
    queryFn: authApi.getVendorProfile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Simple retry count instead of complex logic
  });
}

export function useUpdateVendorProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.updateVendorProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorProfile'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Vendor profile updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}



export function useSignIn() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.signIn,
    onSuccess: (data) => {
      // Don't show success toast if verification is required
      if (!('requiresVerification' in data && data.requiresVerification)) {
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        toast.success('Signed in successfully');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useSignUp() {
  return useMutation({
    mutationFn: authApi.signUp,
    onSuccess: () => {
      toast.success('Account created successfully. Please check your email to verify your account.');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.signOut,
    onSuccess: () => {
      queryClient.clear(); // Clear all cached data
      toast.success('Signed out successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useUploadFile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.uploadFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorProfile'] });
      toast.success('File uploaded successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useDeleteFile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: authApi.deleteFile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorProfile'] });
      toast.success('File deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: authApi.forgotPassword,
    onSuccess: () => {
      toast.success('Password reset instructions sent to your email');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      toast.success('Password reset successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    }
  });
}


